<script setup>
import { computed, ref, onMounted } from 'vue';
import { useToggle } from '@vueuse/core';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';
import subDays from 'date-fns/subDays';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  generateReportURLParams,
  parseReportURLParams,
} from '../helpers/reportFilterHelper';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false,
  },
  showInboxFilter: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['filterChange']);

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();
const inboxes = useMapGetter('inboxes/getInboxes');

const customDateRange = ref([subDays(new Date(), 6), new Date()]);
const selectedDateRange = ref(DATE_RANGE_TYPES.LAST_7_DAYS);
const businessHoursSelected = ref(false);
const selectedInboxId = ref(null);
const [showInboxDropdown, toggleInboxDropdown] = useToggle();

const inboxMenuItems = computed(() => {
  const items = inboxes.value.map(inbox => ({
    label: inbox.name,
    value: inbox.id,
    action: 'select_inbox',
    isSelected: inbox.id === selectedInboxId.value,
  }));

  if (!isAdmin.value) return items;

  return [
    {
      label: t('INBOX_REPORTS.ALL_INBOXES'),
      value: null,
      action: 'select_inbox',
      isSelected: selectedInboxId.value === null,
    },
    ...items,
  ];
});

const selectedInboxLabel = computed(() => {
  const selectedInbox = inboxes.value.find(
    inbox => inbox.id === selectedInboxId.value
  );

  if (selectedInbox) return selectedInbox.name;
  if (isAdmin.value) return t('INBOX_REPORTS.ALL_INBOXES');

  return t('INBOX_REPORTS.FILTER_DROPDOWN_LABEL');
});

const canChangeInbox = computed(
  () => isAdmin.value || inboxMenuItems.value.length > 1
);

const updateURLParams = () => {
  const params = generateReportURLParams({
    from: getUnixStartOfDay(customDateRange.value[0]),
    to: getUnixEndOfDay(customDateRange.value[1]),
    businessHours: businessHoursSelected.value,
    range: selectedDateRange.value,
    inboxId: props.showInboxFilter ? selectedInboxId.value : null,
  });

  router.replace({ query: { ...params } });
};

const emitChange = () => {
  updateURLParams();
  emit('filterChange', {
    from: getUnixStartOfDay(customDateRange.value[0]),
    to: getUnixEndOfDay(customDateRange.value[1]),
    businessHours: businessHoursSelected.value,
    inboxId: props.showInboxFilter ? selectedInboxId.value : null,
  });
};

const onDateRangeChange = value => {
  const [startDate, endDate, rangeType] = value;
  customDateRange.value = [startDate, endDate];
  selectedDateRange.value = rangeType || DATE_RANGE_TYPES.CUSTOM_RANGE;
  emitChange();
};

const onBusinessHoursToggle = () => {
  emitChange();
};

const onInboxAction = ({ value }) => {
  selectedInboxId.value = value;
  toggleInboxDropdown(false);
  emitChange();
};

const initializeFromURL = () => {
  const urlParams = parseReportURLParams(route.query);

  // Set the range type first
  if (urlParams.range) {
    selectedDateRange.value = urlParams.range;
  }

  // Restore dates from URL if available
  if (urlParams.from && urlParams.to) {
    customDateRange.value = [
      new Date(urlParams.from * 1000),
      new Date(urlParams.to * 1000),
    ];
  }

  if (urlParams.businessHours) {
    businessHoursSelected.value = urlParams.businessHours;
  }

  selectedInboxId.value = urlParams.inboxId;
};

const normalizeInboxSelection = () => {
  const requestedInbox = inboxes.value.find(
    inbox => inbox.id === selectedInboxId.value
  );

  if (requestedInbox) return;

  selectedInboxId.value = isAdmin.value ? null : inboxes.value[0]?.id || null;
};

onMounted(async () => {
  initializeFromURL();
  if (props.showInboxFilter) {
    await store.dispatch('inboxes/get');
    normalizeInboxSelection();
  }
  emitChange();
});
</script>

<template>
  <div
    class="flex flex-col justify-between gap-3 md:flex-row"
    :class="{ 'pointer-events-none opacity-50': disabled }"
  >
    <div class="flex flex-col flex-wrap items-start gap-2 md:flex-row">
      <WootDatePicker
        v-model:date-range="customDateRange"
        v-model:range-type="selectedDateRange"
        @date-range-changed="onDateRangeChange"
      />
      <div
        v-if="showInboxFilter"
        v-on-clickaway="() => toggleInboxDropdown(false)"
        class="relative flex items-center"
      >
        <Button
          sm
          slate
          outline
          icon="i-lucide-inbox"
          :label="selectedInboxLabel"
          :disabled="!canChangeInbox"
          class="max-w-56"
          @click="toggleInboxDropdown()"
        />
        <DropdownMenu
          v-if="showInboxDropdown"
          :menu-items="inboxMenuItems"
          show-search
          :search-placeholder="$t('INBOX_REPORTS.SEARCH_INBOX')"
          class="mt-1 ltr:left-0 rtl:right-0 top-full !min-w-56 max-w-56 max-h-96"
          @action="onInboxAction"
        />
      </div>
    </div>
    <div class="flex items-center">
      <span class="mx-2 text-sm whitespace-nowrap">
        {{ $t('REPORT.BUSINESS_HOURS') }}
      </span>
      <span>
        <ToggleSwitch
          v-model="businessHoursSelected"
          @change="onBusinessHoursToggle"
        />
      </span>
    </div>
  </div>
</template>
